/**
 * Sprint M5.23-Ergaenzung (28.07.2026) — Admin-Feedback-Overview
 *
 * Einseitige Admin-Ansicht der vier Feedback-Kollektionen mit
 * Tabs + clientseitigem Filter (Email, Volltext-Suche in message/context).
 *
 * Route: /admin/feedback (nur mit Admin-Login sichtbar; Backend gated).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, RefreshCw, MessageSquare, Sparkles, Bell, CreditCard } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";

const TABS = [
  { id: "feedback",                   label: "Feedback",                Icon: MessageSquare },
  { id: "pionier_feedback",           label: "Pionier-Feedback",        Icon: Sparkles },
  { id: "pro_waitlist_notifications", label: "Sag-Bescheid-Klicks",     Icon: Bell },
  { id: "pro_upgrade_waitlist",       label: "Upgrade-Waitlist",        Icon: CreditCard },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function matchesQuery(row, q) {
  if (!q) return true;
  const hay = JSON.stringify(row).toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function AdminFeedbackOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("feedback");
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/feedback-overview?limit=200");
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 403 || err?.response?.status === 401) {
        setError("Diese Seite ist nur für Admins.");
      } else {
        setError("Feedback konnte nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  // Post-Launch-H4 (10.02.2026) — rows in useMemo wrappen, damit die
  // abhaengige useMemo (filtered) nicht bei jedem Render neu berechnet
  // wird. Fixed ESLint-Deploy-Block.
  const rows = useMemo(() => (data ? (data[activeTab] || []) : []), [data, activeTab]);
  const filtered = useMemo(() => rows.filter((r) => matchesQuery(r, query)), [rows, query]);
  const totals = data?.totals || {};

  return (
    <AdminLayout>
      <div data-testid="admin-feedback-page">
        <header className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">
              Admin · Übersicht
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white" data-testid="admin-feedback-headline">
              Feedback &amp; Signals
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Alle Feedback-Einträge, &bdquo;Sag Bescheid&ldquo;-Klicks und Upgrade-Waitlist-Submits an einem Ort.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-[#4563BB]"
            data-testid="admin-feedback-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Aktualisieren
          </button>
        </header>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm" data-testid="admin-feedback-error">
            {error}
          </div>
        )}

        {loading && !data && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {data && (
          <>
            {/* Tabs */}
            <nav className="flex flex-wrap gap-2 mb-4" data-testid="admin-feedback-tabs">
              {TABS.map((t) => {
                const { Icon } = t;
                const count = totals[t.id] ?? 0;
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition-colors ${
                      active
                        ? "bg-[#4563BB] text-white"
                        : "bg-white border border-slate-200 text-slate-700 hover:border-[#4563BB]"
                    }`}
                    data-testid={`admin-feedback-tab-${t.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {t.label}
                    <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${
                      active ? "bg-white/20" : "bg-slate-100"
                    }`}>{count}</span>
                  </button>
                );
              })}
            </nav>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter (Email, Text, Modul, KT-Tag)"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-[#4563BB] focus:outline-none"
                data-testid="admin-feedback-search"
              />
            </div>

            {/* Table */}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-feedback-table-wrap">
              <div className="max-h-[65vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                      <th className="p-3 font-semibold">Datum</th>
                      <th className="p-3 font-semibold">Email</th>
                      <FeedbackTableColumns tab={activeTab} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-slate-400">
                          Keine Einträge{query ? " für diesen Filter" : ""}.
                        </td>
                      </tr>
                    )}
                    {filtered.map((row) => (
                      <FeedbackRow key={row.id} tab={activeTab} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t border-slate-100 text-xs text-slate-500 flex justify-between">
                <span data-testid="admin-feedback-count">
                  {filtered.length} von {rows.length} sichtbar
                </span>
                <span>Limit 200 Einträge · Sortierung neueste zuerst</span>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function FeedbackTableColumns({ tab }) {
  if (tab === "feedback" || tab === "pionier_feedback") {
    return (
      <>
        <th className="p-3 font-semibold">Typ</th>
        <th className="p-3 font-semibold">Nachricht</th>
        <th className="p-3 font-semibold">Kontext</th>
      </>
    );
  }
  if (tab === "pro_waitlist_notifications") {
    return (
      <>
        <th className="p-3 font-semibold">Modul</th>
        <th className="p-3 font-semibold" colSpan={2}>KT-Tag</th>
      </>
    );
  }
  return (
    <>
      <th className="p-3 font-semibold">Plan</th>
      <th className="p-3 font-semibold" colSpan={2}>KT-Tag</th>
    </>
  );
}

function FeedbackRow({ tab, row }) {
  const dateCell = <td className="p-3 whitespace-nowrap text-slate-600 align-top">{fmtDate(row.created_at)}</td>;
  const emailCell = <td className="p-3 text-slate-800 align-top break-all">{row.email || "—"}</td>;

  if (tab === "feedback" || tab === "pionier_feedback") {
    return (
      <tr className="hover:bg-slate-50">
        {dateCell}
        {emailCell}
        <td className="p-3 align-top">
          <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wider">
            {row.type || "?"}
          </span>
        </td>
        <td className="p-3 align-top text-slate-800">{row.message || <em className="text-slate-400">—</em>}</td>
        <td className="p-3 align-top text-xs text-slate-500">
          {row.context ? (
            <pre className="whitespace-pre-wrap font-mono text-[10px]">{JSON.stringify(row.context, null, 2)}</pre>
          ) : <em>—</em>}
        </td>
      </tr>
    );
  }
  if (tab === "pro_waitlist_notifications") {
    return (
      <tr className="hover:bg-slate-50">
        {dateCell}
        {emailCell}
        <td className="p-3 align-top">
          <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold uppercase tracking-wider">
            {row.module_id || "?"}
          </span>
        </td>
        <td className="p-3 align-top text-xs text-slate-600 font-mono" colSpan={2}>{row.kt_tag || "—"}</td>
      </tr>
    );
  }
  return (
    <tr className="hover:bg-slate-50">
      {dateCell}
      {emailCell}
      <td className="p-3 align-top">
        <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold uppercase tracking-wider">
          {row.plan || "?"}
        </span>
      </td>
      <td className="p-3 align-top text-xs text-slate-600 font-mono" colSpan={2}>{row.kt_tag || "—"}</td>
    </tr>
  );
}
